import { Injectable, computed, effect, inject, signal, untracked } from '@angular/core';
import { Router } from '@angular/router';
import { AuthStore, SyncedCollection } from '@huddle-sports-platform/app-core';
import { accountSchema, type Account } from '@huddle-sports-platform/schema';
import { collection, documentId, query, where } from 'firebase/firestore';

import { db } from '../core/firebase';

/**
 * The routes that welcome the signed-out: the auth doors plus the tokened
 * public pages. When a live session dies (account deleted from HQ, login
 * disabled), anywhere else must fall back to the sign-in door - the route
 * guards only run at NAVIGATION time, so a page already on screen never
 * re-answers them.
 */
const SIGNED_OUT_HOMES = [
  '/sign-in',
  '/sign-up',
  '/forgot-password',
  '/auth/action',
  '/give/',
  '/lottery/',
  '/ticket/',
];

/**
 * The member data store's seed: the signed-in member's OWN `accounts/{uid}`
 * doc, live. Rules allow that read (`isSelf`) and nothing else: every
 * mutation, the member's own name included, goes through a Callable that
 * also carries the change out to the club-side mirrors.
 *
 * `needsOnboarding` is THE onboarding gate, in two parts:
 * - `profileComplete` - first name, last name AND dob are all present
 *   (the wizard's first step writes them; missing any -> start at step 1).
 * - a club affiliation - until the member belongs to or manages a club,
 *   app load returns them to the wizard, resuming at the intent step.
 */
@Injectable({ providedIn: 'root' })
export class AccountStore {
  private readonly auth = inject(AuthStore);
  private readonly router = inject(Router);

  private readonly accountSync = new SyncedCollection<Account>(accountSchema, 'account');

  /** The member's own account doc; undefined until the first snapshot lands. */
  readonly account = computed<Account | undefined>(() => [...this.accountSync.docs().values()][0]);

  /** First name, last name AND dob all present - the wizard's step-1 ask. */
  readonly profileComplete = computed(() => {
    const account = this.account();
    return !!account && !!account.firstName && !!account.lastName && account.dob != null;
  });

  /** They dismissed the profile ask this run. In memory, deliberately -
   *  the same stance NotificationAccess takes on "not now": a new session
   *  is a fair moment to ask again, and nothing about a dismissal is worth
   *  persisting. */
  private readonly _profileAskSkipped = signal(false);

  /** Ask for the profile on the way in? Incomplete AND not yet waved away.
   *  This is a NUDGE, not a gate: `Landing` sends a signed-in member here
   *  first, and the page's back button dismisses it. What an incomplete
   *  profile actually costs is enforced where it matters - `joinClub`
   *  refuses one server-side, so nobody half-named joins a club or
   *  registers. */
  readonly shouldAskProfile = computed(() => !this.profileComplete() && !this._profileAskSkipped());

  /** "Not now" - see {@link shouldAskProfile}. */
  skipProfileAsk(): void {
    this._profileAskSkipped.set(true);
  }

  /** True until the profile is complete AND the member has a club. */
  readonly needsOnboarding = computed(() => {
    const account = this.account();
    if (!account) return true;
    return (
      !this.profileComplete() || (account.clubIds.length === 0 && account.manages.length === 0)
    );
  });

  /** Sync health, for future screens that surface it. */
  readonly live = this.accountSync.live;
  readonly warming = this.accountSync.warming;

  /** The current warm cycle delivered its first server snapshot (or died -
   *  the pages own the error surface). The signal twin of whenLoaded(),
   *  for the root boot gate's data-init readiness. */
  readonly loaded = computed(
    () => this.accountSync.failed() || (this.accountSync.attached() && !this.accountSync.warming()),
  );

  /** The last claims stamp this session acted on - tracked by value so
   *  ordinary account writes (name edits, club joins) never churn tokens. */
  private lastClaimsStamp: number | undefined;

  /** The uid the CURRENT warm cycle serves - undefined until the effect has
   *  run at all, null when signed out. What {@link whenLoaded} matches
   *  against the live auth uid, so a guard can never be answered by the
   *  PREVIOUS account's snapshot during an in-place account switch. */
  private cycleUid: string | null | undefined;

  /** The uid whose account doc has been SEEN this session - the dead-session
   *  watcher only fires on an exists -> gone transition, so a brand-new
   *  sign-up (signed in, doc not provisioned yet) is never mistaken for a
   *  deleted account. */
  private sawAccountForUid: string | undefined;

  constructor() {
    effect(() => {
      const user = this.auth.user();
      this.cycleUid = user?.uid ?? null;
      if (user) {
        this.accountSync.warm(
          query(collection(db(), 'accounts'), where(documentId(), '==', user.uid)),
        );
      } else {
        this.accountSync.detach();
      }
    });

    // THE DEAD-SESSION WATCHER. HQ can delete an account outright while its
    // owner is mid-session: the Firebase session stays "signed in" until the
    // token expires, the accounts/{uid} listener delivers an empty snapshot,
    // and needsOnboarding would read that as a fresh account and walk the
    // ghost into the profile wizard. An account that EXISTED and is now gone
    // for the same uid is a deletion, never onboarding - end the session.
    // (Disabled logins take the other road: their revoked tokens fail
    // refresh within the hour, auth flips to null, and the auth-lost effect
    // below brings them to the door.)
    effect(() => {
      const user = this.auth.user();
      if (!user || this.cycleUid !== user.uid) return;
      const account = this.account();
      if (account) {
        this.sawAccountForUid = user.uid;
        return;
      }
      if (!this.accountSync.attached() || this.accountSync.warming() || this.accountSync.failed())
        return;
      if (this.sawAccountForUid !== user.uid) return;
      this.sawAccountForUid = undefined;
      void this.auth.signOut();
    });

    // AUTH LOST -> the sign-in door. Guards answer navigations, not pages
    // already on screen - when the session ends mid-page (the watcher above,
    // a disabled login's failed token refresh, a sign-out from another tab),
    // whatever signed-in screen is showing must yield. The tokened public
    // pages and the auth doors are exempt: they welcome the signed-out.
    let hadUser = false;
    effect(() => {
      const user = this.auth.user();
      if (user) {
        hadUser = true;
        return;
      }
      if (!hadUser) return;
      hadUser = false;
      const url = untracked(() => this.router.url);
      if (SIGNED_OUT_HOMES.some((home) => url === home || url.startsWith(home))) return;
      void this.router.navigateByUrl('/sign-in');
    });

    // Claims follow the access docs server-side (the adminOf club-admin
    // claim); when the account's claimsSyncedAt stamp moves, silently
    // pull a FRESH id token so the change applies immediately - the
    // storage rules read the claim on upload. The FIRST observation is
    // skipped: a just-signed-in token already carries current claims.
    effect(() => {
      const account = this.account();
      if (!account) {
        this.lastClaimsStamp = undefined;
        return;
      }
      const stamp = account.claimsSyncedAt;
      if (stamp === undefined || stamp === this.lastClaimsStamp) return;
      const isFirstObservation = this.lastClaimsStamp === undefined;
      this.lastClaimsStamp = stamp;
      if (isFirstObservation) return;
      void this.auth.user()?.getIdToken(true);
    });
  }

  /**
   * Resolves once the warm cycle FOR THE ACCOUNT SIGNED IN NOW has its
   * first server snapshot (or has failed - the guard then falls through to
   * the profile form, whose submit surfaces the real error).
   *
   * The uid match is the load-bearing part: signing in as B while A's
   * snapshot is still attached (sign-out then sign-in, or the sign-up
   * page's existing-login fold-down) used to let a guard read A's account
   * for B's navigation - in a locked app that walked B through the club
   * gate on A's membership. The poll waits for the re-warm effect to catch
   * up with the switch before answering.
   */
  async whenLoaded(): Promise<void> {
    for (;;) {
      // No early answer while signed out: at boot the guards can run BEFORE
      // Firebase restores the persisted user, and answering then would send
      // a member through the gates with no account at all. Keep polling -
      // either the restore lands and the real answer follows, or the
      // navigation is torn down by the auth guards. (This is what the
      // pre-uid-match version did implicitly via the attached() poll.)
      const uid = this.auth.user()?.uid ?? null;
      if (uid !== null && this.cycleUid === uid) {
        if (this.accountSync.failed()) return;
        if (this.accountSync.attached() && !this.accountSync.warming()) return;
      }
      await new Promise((resolve) => setTimeout(resolve, 50));
    }
  }
}
